import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { FileRouteTypes } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import {
  getMultiFactorResolver,
  isSignInWithEmailLink,
  signInWithEmailLink,
  type MultiFactorError,
} from 'firebase/auth';
import { auth } from '../constants/firebaseModular';
import { usePrompt } from '../hooks/usePrompt';
import { Prompt } from '../components/Prompt';
import * as Sentry from '@sentry/browser';
import { useAuth } from '../contexts/AuthContext';
import { MfaChallengeForm } from '../components/auth/MfaChallengeForm';
import type { Resolver } from '../hooks/useMfaChallenge';

type ConciergeDestination = {
  destination: string;
  apiKey: string;
  oobCode: string;
  mode: string;
  language: string;
  email?: string;
};

type SignInError = {
  code: string;
  message: string;
} | null;

const MAX_NETWORK_RETRIES = 3;

function getErrorDisplay(error: SignInError, retryCount: number) {
  if (!error) return null;

  if (error.code === 'auth/network-request-failed' && retryCount < MAX_NETWORK_RETRIES) {
    return {
      message: "We're having trouble connecting. Please check your internet connection and try again.",
      canRetry: true,
    };
  }

  if (error.code === 'auth/network-request-failed') {
    return {
      message:
        'We were unable to connect after multiple attempts. Please check your internet connection and request a new link from your concierge.',
      canRetry: false,
    };
  }

  if (error.code === 'auth/invalid-action-code') {
    return {
      message: 'This link has expired or was already used. Please request a new link from your concierge.',
      canRetry: false,
    };
  }

  // ⚠️ DEFENCE IN DEPTH, NOT THE PRIMARY PATH (CU-86akjgb8p, scope 1).
  //
  // The resolver branch in `attemptSignIn` returns early and renders the
  // challenge, so this case should be unreachable in the happy path. It exists
  // because the failure it replaces was a LOOP: the old generic message told
  // the patient to request a new link, the concierge reissued one, and it
  // failed identically — the failure was never about the link. If the resolver
  // branch ever fails to engage, this at least tells the truth.
  //
  // ⚠️ Never route this to "request a new link".
  if (error.code === 'auth/multi-factor-auth-required') {
    return {
      message:
        'This account needs an extra verification step, but we could not start it. Please sign in with your email and password instead, or contact info@mideohealth.com.',
      canRetry: false,
    };
  }

  // The challenge was dismissed. The link is already spent (the oobCode is
  // consumed BEFORE Identity Platform raises the MFA error), so "request a new
  // link" would send them around the loop again — the password is the durable
  // root, so point there.
  if (error.code === 'concierge/challenge-cancelled') {
    return {
      message:
        'This sign-in link has now been used. You can sign in with your email and password, or request a new link from your concierge.',
      canRetry: false,
    };
  }

  return {
    message: 'Something went wrong signing in. Please request a new link from your concierge.',
    canRetry: false,
  };
}

export const Route = createFileRoute('/concierge')({
  validateSearch: (search: Record<string, string>): ConciergeDestination => {
    return {
      destination: search.destination,
      apiKey: search.apiKey,
      oobCode: search.oobCode,
      mode: search.mode,
      language: search.language,
      email: search.email,
    };
  },
  component: Concierge,
});

function Concierge() {
  const { destination, email: urlEmail } = Route.useSearch();
  const navigate = useNavigate();
  const { triggerPrompt } = usePrompt();
  const { firebaseSignOut } = useAuth();

  // Priority: URL email > localStorage email > undefined
  const storedEmail = window.localStorage.getItem('emailForSignIn');
  const [email, setEmail] = useState<string | undefined>(
    urlEmail || storedEmail || undefined, // ← Use urlEmail here
  );

  const [hasPrompted, setHasPrompted] = useState(false);
  const [signInError, setSignInError] = useState<SignInError>(null);
  const [retryCount, setRetryCount] = useState(0);
  /**
   * The pending MFA challenge, when the magic link returned one (CU-86akjgb8p).
   *
   * ⚠️ LOCAL STATE ONLY, NEVER ROUTED TO. The resolver is a live object holding
   * the in-flight sign-in; it does not survive serialization through router
   * params or storage. See the fuller note in `login.tsx`.
   *
   * ⚠️ While this is set, `auth.currentUser` is still NULL — the MFA error means
   * sign-in did NOT complete. That is what keeps the already-authenticated
   * effect below from firing mid-challenge.
   */
  const [mfaResolver, setMfaResolver] = useState<Resolver | null>(null);

  // Store URL email and clear from URL immediately for security
  useEffect(() => {
    if (urlEmail) {
      // Store in localStorage as backup
      window.localStorage.setItem('emailForSignIn', urlEmail);
      setEmail(urlEmail);

      // Clear email from URL to prevent exposure in browser history
      const url = new URL(window.location.href);
      url.searchParams.delete('email');
      window.history.replaceState({}, '', url.toString());
    }
  }, [urlEmail]);

  const handleEmailSubmit = (email: string) => {
    // SET IN STATE FOR FUNCTION CALL
    setEmail(email);

    // SET IN LOCAL STORAGE FOR CONTINUED USE
    window.localStorage.setItem('emailForSignIn', email);
  };

  const captureWithContext = useCallback(
    (error: unknown, attemptNumber: number) => {
      Sentry.setContext('conciergeSignIn', {
        errorCode: (error as { code?: string })?.code,
        networkOnline: navigator.onLine,
        attemptNumber,
        hasEmail: !!email,
        emailSource: urlEmail ? 'url' : storedEmail ? 'localStorage' : 'prompt',
        destination,
      });
      Sentry.captureException(error);
    },
    [email, urlEmail, storedEmail, destination],
  );

  /**
   * Everything that must happen once the session actually exists — CU-86akjgb8p.
   *
   * ⚠️ ONE IMPLEMENTATION, TWO CALLERS, AND THAT IS THE POINT. `/concierge`
   * differs from `/login` and `/enroll`: success here is not merely "a session
   * exists", it is "a session exists AND the single-use credentials are
   * scrubbed AND we navigated to `destination`". This used to live inline in
   * the `.then()`. The MFA challenge introduced a SECOND success path, and two
   * copies of this would drift — invisibly, because the direct path would still
   * pass every test while the post-challenge path quietly stopped scrubbing the
   * oobCode out of the URL.
   */
  /**
   * Where to send the patient once sign-in succeeds.
   *
   * ⚠️ `destination` is a URL query param, so it is OPTIONAL — `validateSearch`
   * passes `search.destination` straight through and it is `undefined` whenever
   * the link omits it. Both call sites used to do `destination.startsWith('/')`
   * unguarded, which threw `undefined is not an object` and dropped the patient
   * on the "Something went wrong" boundary (Sentry MIDEO-UNIFIED-BB, found by
   * device testing 2026-09-21 with a spent link and an authenticated session).
   *
   * Falling back to `/` sends them to the portal root, which routes an
   * authenticated patient onward — the same place they would land from a bare
   * visit. CU-86akjut0z.
   */
  const resolveDestination = useCallback(
    (): FileRouteTypes['to'] =>
      (!destination ? '/' : destination.startsWith('/') ? destination : `/${destination}`) as FileRouteTypes['to'],
    [destination],
  );

  const completeSignIn = useCallback(() => {
    // Clear sensitive data from localStorage
    window.localStorage.removeItem('emailForSignIn');

    // Clear all sensitive parameters from URL
    const url = new URL(window.location.href);
    url.searchParams.delete('email');
    url.searchParams.delete('oobCode');
    url.searchParams.delete('apiKey');
    window.history.replaceState({}, '', url.toString());

    // Navigate to final destination
    setSignInError(null);
    navigate({ to: resolveDestination() });
  }, [resolveDestination, navigate]);

  const attemptSignIn = useCallback(() => {
    if (!email) return;

    signInWithEmailLink(auth, email, window.location.href)
      .then(completeSignIn)
      .catch((error) => {
        // ⚠️ THIS BRANCH MUST COME FIRST, AND THE CREDENTIAL WAS ACCEPTED.
        //
        // Identity Platform raises `auth/multi-factor-auth-required` when the
        // magic link was VALID but the account holds a second factor. It is a
        // CONTINUATION, not a failure: a resolver carrying the pending sign-in
        // is attached to the error, and the app is expected to render the SMS
        // challenge and complete it.
        //
        // The blanket catch that used to be here told the patient "Something
        // went wrong signing in. Please request a new link from your
        // concierge" — so the concierge reissued a link that failed
        // identically. Confirmed in production: Sentry MIDEO-UNIFIED-B5,
        // 5 events / 2 users, 2026-09-15. The same bug class locked an enrolled
        // admin out of the console on staging (2026-07-30) and locked out
        // mobile's test account before that.
        //
        // ⚠️ Returning early is load-bearing: it skips `retryCount` (so the
        // Retry button never appears under a live challenge, where it would
        // re-POST a consumed oobCode), skips `setSignInError` (which would
        // render the failure panel underneath the challenge), and skips the
        // Sentry capture below.
        const code = (error as { code?: string })?.code ?? '';
        if (code === 'auth/multi-factor-auth-required') {
          try {
            // ⚠️ The resolver is a LIVE object carrying the pending sign-in. It
            // cannot be serialized — never put it in router state,
            // sessionStorage or a URL. Local state only. A reload loses it, and
            // because the oobCode is already spent the patient falls through to
            // `auth/invalid-action-code`, which has honest copy.
            setMfaResolver(getMultiFactorResolver(auth, error as MultiFactorError) as unknown as Resolver);
            // Deliberately NOT captured to Sentry: this is expected control
            // flow and would otherwise fire for every enrolled patient on every
            // concierge link.
            Sentry.addBreadcrumb({
              category: 'mfa-sms',
              level: 'info',
              message: 'concierge: second factor required',
            });
          } catch (resolverError) {
            // The resolver could not be built from a genuine MFA challenge —
            // the patient cannot proceed and cannot self-diagnose, so this must
            // be visible. `getErrorDisplay` renders honest copy for the code.
            Sentry.captureException(resolverError, {
              tags: { feature: 'mfa-sms', step: 'concierge-resolver' },
            });
            setSignInError({ code: 'auth/multi-factor-auth-required', message: 'resolver unavailable' });
          }
          return;
        }

        const newRetryCount = retryCount + 1;
        setRetryCount(newRetryCount);
        setSignInError({ code: error.code, message: error.message });
        captureWithContext(error, newRetryCount);
      });
  }, [email, completeSignIn, retryCount, captureWithContext]);

  useEffect(() => {
    // Only prompt for email if this is a valid sign-in link AND we don't have the email AND we haven't prompted yet
    if (isSignInWithEmailLink(auth, window.location.href) && !email && !hasPrompted) {
      setHasPrompted(true);
      try {
        triggerPrompt({
          title: 'Please provide the email address associated with your account.',
          text: '',
          isInput: true,
          onSubmit: (title: string) => handleEmailSubmit(title),
        });
      } catch (error) {
        Sentry.captureException(error);
        console.error('Error triggering prompt. Error: ', error);
      }
    }
  }, [email, hasPrompted, triggerPrompt]);

  useEffect(() => {
    // ⚠️ THE CHALLENGE OWNS THE SIGN-IN WHILE IT IS PENDING (CU-86akjgb8p,
    // risk 4). `completeSignIn` is what navigates after `resolveSignIn`
    // succeeds; without this guard a re-render while the resolver is live could
    // reach `attemptSignIn()` below and re-POST an oobCode that Identity
    // Platform has already consumed.
    //
    // ⚠️ This is NOT redundant with the `auth.currentUser` check. `currentUser`
    // is null for the whole challenge, so the FIRST branch is already
    // unreachable — it is the `attemptSignIn()` fallthrough at the bottom of
    // this effect that this guard actually stops.
    if (mfaResolver) return;

    if (auth.currentUser && email) {
      // User already authenticated
      if (auth.currentUser.email?.toLowerCase().trim() === email?.toLowerCase().trim()) {
        // Same user - navigate directly to destination
        navigate({ to: resolveDestination() });
        return;
      } else {
        // Different user - auto sign out and allow new sign in
        setSignInError({
          code: 'auth/user-mismatch',
          message:
            'You are already signed in as a different user. You will now be signed out automatically. Please close this tab and request a new magic link from your concierge.',
        });
        firebaseSignOut()
          .then(() => {
            console.log('User signed out successfully, ready for new sign-in');
          })
          .catch((error) => {
            console.error('Error signing out: ', error);
            Sentry.captureException(error);
            setSignInError({
              code: 'auth/sign-out-failed',
              message: 'Failed to sign out. Please try again or contact support.',
            });
          });
        return;
      }
    }
    if (email) {
      attemptSignIn();
    }
    // ⚠️ `mfaResolver` is in the dep array so the guard above is re-evaluated
    // when a challenge starts. The remaining omissions (`attemptSignIn`,
    // `firebaseSignOut`) are PRE-EXISTING and deliberately left alone — adding
    // `attemptSignIn` would re-run this effect on every `retryCount` change and
    // re-POST the consumed oobCode. Out of scope for CU-86akjgb8p.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, resolveDestination, navigate, mfaResolver]);

  const errorDisplay = getErrorDisplay(signInError, retryCount);

  /**
   * ⚠️ THE CHALLENGE REPLACES the page rather than stacking over it, and is
   * checked BEFORE `errorDisplay` — a stale error panel behind a live challenge
   * would tell the patient the sign-in failed while they are mid-way through
   * completing it.
   *
   * `auth.currentUser` is still null here, so none of the effects above have
   * navigated.
   */
  if (mfaResolver) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6 px-4">
        <img
          src="https://storage.googleapis.com/mideoid-console-public/logos/MIDEOhealth.png"
          alt="mideo_logo"
          className="h-24"
        />
        <MfaChallengeForm
          resolver={mfaResolver}
          // The address the link was issued to. ⚠️ A CLAIM, not a verified
          // identity — the second factor is unproven at this point.
          claimedEmail={email}
          onComplete={() => {
            // ⚠️ THE SAME cleanup the direct path runs. The oobCode and apiKey
            // are still in the URL and `emailForSignIn` is still in
            // localStorage, because the `.then()` never ran — the MFA error
            // diverted us before it.
            setMfaResolver(null);
            completeSignIn();
          }}
          onCancel={() => {
            // ⚠️ The link is ALREADY SPENT — `signInWithEmailLink` consumes the
            // oobCode before Identity Platform raises the MFA error, so there
            // is nothing to return to. Say so plainly and point at the durable
            // root (email + password) rather than at another dead link.
            setMfaResolver(null);
            setSignInError({
              code: 'concierge/challenge-cancelled',
              message: 'challenge cancelled by patient',
            });
          }}
        />
      </div>
    );
  }

  return (
    <>
      {!email && <Prompt />}
      {errorDisplay && (
        <div style={{ padding: '2rem', textAlign: 'center', maxWidth: '480px', margin: '0 auto' }}>
          <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>{errorDisplay.message}</p>
          {errorDisplay.canRetry && (
            <button
              onClick={attemptSignIn}
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                cursor: 'pointer',
                borderRadius: '4px',
                border: '1px solid #ccc',
                backgroundColor: '#007bff',
                color: '#fff',
              }}
            >
              Retry
            </button>
          )}
          {/* ⚠️ THE NEVER-TRAPPED ESCAPE. Every terminal state on this route
              ends with a spent link, so the password is the only way forward —
              and a message with no affordance reads as a dead end. */}
          {!errorDisplay.canRetry && (
            <button
              onClick={() => navigate({ to: '/login' })}
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                cursor: 'pointer',
                borderRadius: '4px',
                border: '1px solid #ccc',
                backgroundColor: '#007bff',
                color: '#fff',
              }}
            >
              Sign in with email and password
            </button>
          )}
        </div>
      )}
    </>
  );
}
